import { useState, useEffect } from "react";
import { ChevronDown, Calendar, Clock, TrendingUp, BarChart3, LineChart, Layers, Target } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  readTime: string;
  category: string;
  icon: typeof TrendingUp;
}

const blogPosts: BlogPost[] = [
  {
    id: "mastering-market-profile",
    title: "Mastering Market Profile TPO Charts in Sierra Chart",
    excerpt: "Learn how to read and interpret Market Profile TPO charts to identify value areas, point of control, and key trading levels for futures markets.",
    content: `Market Profile TPO (Time Price Opportunity) charts are one of the most powerful tools available to futures traders. Unlike traditional candlestick charts, TPO charts organize price data by time spent at each price level, giving you a clear picture of where the market finds value.

Understanding the key components is essential. The Point of Control (POC) represents the price level where the most trading activity occurred during a session. This is often a magnet for price action and serves as a strong support or resistance level. The Value Area, typically encompassing 70% of the trading activity, shows you where the majority of business was conducted.

When setting up your Market Profile in Sierra Chart, start with the default TPO letter display. Each letter represents a 30-minute time bracket. As you become more comfortable, you can customize the time brackets and overlay volume profiles for additional confluence.

Key trading strategies using Market Profile include trading the Initial Balance breakout, fading moves outside the Value Area back toward the POC, and identifying single prints that often act as support and resistance. Our pre-configured Market Profile templates come with all these elements properly set up, including color-coded value areas, highlighted POC levels, and integrated volume data.

For best results, combine Market Profile analysis with orderflow data. When you see aggressive buying or selling at the edges of the Value Area, it provides high-probability trade setups. Our templates integrate these tools seamlessly, saving you hours of manual configuration.`,
    date: "2025-01-15",
    readTime: "8 min read",
    category: "Market Profile",
    icon: BarChart3,
  },
  {
    id: "orderflow-trading-guide",
    title: "A Complete Guide to Orderflow Trading with Sierra Chart",
    excerpt: "Discover how to use footprint charts, delta analysis, and absorption indicators to gain an edge in your trading decisions.",
    content: `Orderflow trading gives you a microscopic view of market activity that traditional chart analysis simply cannot provide. By analyzing the actual transactions occurring at each price level, you can see the battle between buyers and sellers in real time.

Footprint charts are the foundation of orderflow analysis. They display the volume traded at bid and ask prices within each candle, allowing you to see aggressive buying (market orders hitting the ask) and aggressive selling (market orders hitting the bid). The delta, which is the difference between ask volume and bid volume, reveals the net aggression at each price level.

One of the most powerful orderflow concepts is absorption. This occurs when large limit orders absorb aggressive market orders without price moving. For example, if you see heavy selling (high bid volume) but price refuses to go lower, it suggests that large buyers are absorbing the selling pressure with limit buy orders. This often precedes a reversal.

In Sierra Chart, setting up proper orderflow analysis requires careful configuration of multiple studies. Our Orderflow Suite indicator handles this automatically, providing you with color-coded footprint data, cumulative delta, and absorption detection all in one package.

Key patterns to watch for include delta divergence (price making new highs while delta makes lower highs), stacked imbalances (consecutive price levels showing heavy one-sided aggression), and volume clusters at key levels. These patterns, when combined with Market Profile context, create high-probability trading opportunities.

Our pre-built templates include all necessary configurations for footprint charts, delta displays, and absorption alerts, letting you focus on analysis rather than setup.`,
    date: "2025-01-08",
    readTime: "10 min read",
    category: "Orderflow",
    icon: Layers,
  },
  {
    id: "volume-profile-strategies",
    title: "Volume Profile Strategies for Day Trading Futures",
    excerpt: "Explore practical volume profile strategies that help identify high-probability trade entries and exits in ES, NQ, and CL futures.",
    content: `Volume Profile is distinct from Market Profile in that it focuses purely on volume traded at each price level rather than time spent. This gives you an unbiased view of where the most significant trading activity has occurred, making it invaluable for identifying support, resistance, and potential breakout levels.

The key elements of Volume Profile include the Volume Point of Control (VPOC), which is the single price with the highest traded volume; the Value Area High (VAH) and Value Area Low (VAL), which define the range containing 70% of total volume; and High Volume Nodes (HVN) and Low Volume Nodes (LVN).

High Volume Nodes act as magnets for price. When price approaches an HVN, it tends to slow down and consolidate as it moves through the heavy volume. These are areas of acceptance where both buyers and sellers agree on value. Low Volume Nodes, conversely, are areas of rejection. Price tends to move quickly through these zones, making them excellent targets for breakout trades.

For day trading ES futures, a powerful strategy involves using the developing Volume Profile alongside the previous day's settled profile. Look for the current session's VPOC to migrate toward or away from the previous day's VPOC. A migrating VPOC indicates trend continuation, while a stable VPOC near the previous day's level suggests range-bound conditions.

Another effective approach is the "naked VPOC" strategy. When a session's VPOC is never revisited by price in subsequent sessions, it becomes a naked VPOC and acts as a strong magnet for future price action. Tracking these levels across multiple sessions gives you a roadmap of high-probability targets.

Our Volume Profile templates come pre-configured with composite profiles, developing session profiles, and multi-day VPOC tracking, making it easy to implement these strategies from day one.`,
    date: "2024-12-20",
    readTime: "9 min read",
    category: "Volume Profile",
    icon: LineChart,
  },
  {
    id: "sierra-chart-template-setup",
    title: "How to Set Up and Customize Sierra Chart Templates Like a Pro",
    excerpt: "Step-by-step guide to importing, customizing, and optimizing Sierra Chart templates for your personal trading workflow.",
    content: `Getting the most out of Sierra Chart templates requires understanding how the platform handles chartbooks, studies, and configurations. This guide walks you through the entire process from importing your first template to customizing it for your specific trading style.

Start by downloading your template file from the Purchases page. Template files come as .cht (chartbook) files that contain all chart configurations, studies, color schemes, and layout settings. To import, open Sierra Chart and navigate to File, then Open Chartbook, and select your downloaded file. The template will open as a new chartbook without affecting your existing setups.

Once imported, you will want to customize the template for your specific instruments and data feed. Right-click on any chart and select Chart Settings to modify the symbol, timeframe, and data feed. Most of our templates are configured for popular futures contracts (ES, NQ, CL, GC) but can be adapted to any instrument Sierra Chart supports.

Color customization is one of the most common modifications traders make. Go to the Chart Settings and then the Chart Style tab to adjust background colors, candle colors, and grid settings. For studies like Volume Profile or Market Profile, right-click the study on the chart and select Study Settings to modify colors, transparency, and display options.

Layout management is another important skill. Sierra Chart supports multiple charts within a single chartbook, arranged in customizable layouts. Use the Window menu to arrange charts side by side, stack them vertically, or create tabbed layouts. Save your customized layout by pressing Ctrl+S or going to File and then Save Chartbook.

For optimal performance, consider these tips: limit the number of historical days loaded for intraday charts, disable studies you are not actively using, and use Sierra Chart's built-in study caching to reduce CPU usage. Our templates are already optimized for performance, but these tips help if you add additional studies.`,
    date: "2024-12-10",
    readTime: "7 min read",
    category: "Getting Started",
    icon: Target,
  },
  {
    id: "heatmap-dom-trading",
    title: "Using Heatmaps and DOM for Precision Trade Entries",
    excerpt: "Learn how to combine Sierra Chart heatmaps with depth of market data to time your entries with precision and spot institutional activity.",
    content: `Heatmaps and the Depth of Market (DOM) ladder are two of the most powerful real-time tools available in Sierra Chart. When used together, they provide unparalleled insight into current market microstructure and help you time entries with surgical precision.

A heatmap visualizes limit order book depth over time. Each pixel represents the quantity of resting limit orders at a given price level and time. As time progresses, you can see how the limit order landscape evolves. Large clusters of limit orders appear as bright spots on the heatmap, often indicating institutional interest at specific price levels.

Key heatmap patterns include spoofing (large orders that appear and disappear quickly), iceberg orders (persistent but relatively small visible orders that keep getting refilled), and absorption walls (large resting orders that hold despite aggressive market orders hitting them). Recognizing these patterns in real time gives you a significant edge.

The DOM ladder complements the heatmap by showing you the current state of the order book in numerical format. Sierra Chart's DOM displays bid and ask quantities at each price level, along with recent trades, pull counts, and cumulative delta. When you see a large bid on the DOM that also shows up as a persistent bright spot on the heatmap, you have strong confluence for support at that level.

For practical trading, consider this approach: use the heatmap to identify levels where institutional orders are clustered, then switch to the DOM to monitor how those orders interact with incoming market orders. If a large bid absorbs aggressive selling without price moving lower, it is a high-probability long setup. Confirm with footprint delta showing a shift from negative to positive.

Our Heatmap and DOM templates come pre-configured with optimal color gradients, sensitivity settings, and integrated tools that make it easy to spot these patterns without hours of manual tuning.`,
    date: "2024-11-28",
    readTime: "11 min read",
    category: "Heatmaps & DOM",
    icon: TrendingUp,
  },
];

function BlogPostCard({ post }: { post: BlogPost }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = post.icon;

  return (
    <Card className="overflow-visible" data-testid={`card-blog-post-${post.id}`}>
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-xs" data-testid={`badge-category-${post.id}`}>
              <Icon className="w-3 h-3 mr-1" />
              {post.category}
            </Badge>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
          </div>
        </div>

        <h2 className="text-lg sm:text-xl font-semibold mt-3 mb-2" data-testid={`text-blog-title-${post.id}`}>
          {post.title}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed" data-testid={`text-blog-excerpt-${post.id}`}>
          {post.excerpt}
        </p>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 flex items-center gap-1 text-sm font-medium text-foreground hover:text-muted-foreground transition-colors"
          aria-expanded={isExpanded}
          aria-controls={`blog-content-${post.id}`}
          data-testid={`button-read-more-${post.id}`}
        >
          {isExpanded ? "Show less" : "Read full article"}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
          />
        </button>

        {isExpanded && (
          <div id={`blog-content-${post.id}`} className="mt-4 pt-4 border-t border-border" data-testid={`content-blog-full-${post.id}`}>
            {post.content.split("\n\n").map((paragraph, idx) => (
              <p key={idx} className="text-sm text-muted-foreground leading-relaxed mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function BlogPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Sierra Chart Pro Blog",
    description: "Trading insights, strategies, and guides for Sierra Chart traders.",
    blogPost: blogPosts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
    })),
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO
        title="Blog - Trading Insights & Guides"
        description="Explore trading strategies, Sierra Chart tutorials, and market analysis guides. Learn about Market Profile, orderflow, volume profiles, and more."
        keywords="Sierra Chart blog, trading strategies, Market Profile guide, orderflow trading, volume profile strategies, futures trading tips"
        canonical="/blog"
        jsonLd={jsonLd}
      />

      <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="text-blog-heading">Blog</h1>
      <p className="text-muted-foreground mb-8" data-testid="text-blog-description">
        Trading insights, strategy guides, and tips for getting the most out of Sierra Chart.
      </p>

      <div className="space-y-5">
        {blogPosts.map((post) => (
          <BlogPostCard key={post.id} post={post} />
        ))}
      </div>
    </div>
  );
}
